/**
 * Relateds Data Management Composable
 * Single Responsibility: Manages relateds data state and initialization
 */
import { ref, computed } from '@vue/composition-api';

export function useRelatedsData() {
  // State
  const relateds = ref([]);
  const victim = ref({});
  const selectRelated = ref({});
  const found = ref(true);

  // Progress tracking for different item types
  const hadFoundExhibitIds = ref([]);
  const hadFoundRelatedIds = ref([]);
  const hadFoundTimelineIds = ref([]);
  const hadFoundPositionIds = ref([]);

  /**
   * Initialize relateds data from API response
   * @param {Object} data - API response data
   */
  const initRelatedsData = (data) => {
    // Filter victim from relateds
    victim.value = (data.relateds || []).filter(related => related.status === 'victim');

    // Get non-victim relateds and set found property
    const progress = data.progress || [];
    relateds.value = (data.relateds || [])
      .filter(related => related.status !== 'victim')
      .map(related => ({
        ...related,
        found: related.status === 'default' || progress.includes(related.id)
      }));

    // Select first found related
    selectRelated.value = relateds.value.find(related => related.found) || {};

    return data;
  };

  /**
   * Set selected related
   * @param {Object} related - Selected related object
   */
  const setSelectRelated = (related) => {
    selectRelated.value = related;
    found.value = true;
  };

  /**
   * Find related by name or keyword
   * @param {string} query - Search query
   * @returns {Object|null} Found related or null
   */
  const findRelatedByQuery = (query) => {
    return relateds.value.find(
      related => related.name === query || 
                 (related.keywords && related.keywords.includes(query))
    );
  };

  /**
   * Update found items for specific type
   * @param {string} itemType - Type of item (exhibit, related, timeline, position)
   * @param {Array} foundIds - Array of found item IDs
   */
  const updateFoundItems = (itemType, foundIds) => {
    switch (itemType) {
      case 'exhibit':
        hadFoundExhibitIds.value = foundIds;
        break;
      case 'related':
        hadFoundRelatedIds.value = foundIds;
        break;
      case 'timeline':
        hadFoundTimelineIds.value = foundIds;
        break;
      case 'position':
        hadFoundPositionIds.value = foundIds;
        break;
      default:
        console.warn(`Unknown item type: ${itemType}`);
    }
  };

  /**
   * Get found items by type
   * @param {string} itemType - Type of item
   * @returns {Array} Array of found item IDs
   */
  const getFoundItems = (itemType) => {
    switch (itemType) {
      case 'exhibit':
        return hadFoundExhibitIds.value;
      case 'related':
        return hadFoundRelatedIds.value;
      case 'timeline':
        return hadFoundTimelineIds.value;
      case 'position':
        return hadFoundPositionIds.value;
      default:
        return [];
    }
  };

  // Computed properties
  const hasRelateds = computed(() => relateds.value.length > 0);
  const hasVictim = computed(() => Object.keys(victim.value).length > 0);

  return {
    // State
    relateds,
    victim,
    selectRelated,
    found,
    hadFoundExhibitIds,
    hadFoundRelatedIds,
    hadFoundTimelineIds,
    hadFoundPositionIds,
    
    // Computed
    hasRelateds,
    hasVictim,
    
    // Methods
    initRelatedsData,
    setSelectRelated,
    findRelatedByQuery,
    updateFoundItems,
    getFoundItems
  };
}